import axios from 'axios'
import store from '../store'
import Vue from 'vue'
import { AppVersion, OS, OSVersion } from '../deviceDetails'
import { Success } from '@/statusCodes.js'

const baseURL = `${process.env.VUE_APP_BASE_URL}api/d`;

const axiosInstance = axios.create({
	baseURL,
	headers: {
		OS: 'Windows',
		OSVersion: '13.0',
		AppVersion,
		Platform: 'Web'
	},
	timeout: 11111000 // 30 sec timeout
})

let isRefreshingToken = false
const refreshAndRetryQueue = []

axiosInstance.interceptors.request.use((config) => {
	if (!config.url.includes('/Login') && !config.url.includes('/RefreshToken')) {
		config.headers['Authorization'] = store.getters.getToken;
		config.headers['UserId'] = store.getters.getUserId;
		config.headers['ScreenName'] = localStorage.getItem('isSubUser') === 'true'
			? "Profile"
			: store.getters.getScreenName;
		config.headers['x-api-version'] = '1.0';
		
		const deviceId = localStorage.getItem('deviceId') || 
										 localStorage.getItem('ip') || 
										 store.getters['locationStore/getLocationIpAddress'] || 
										 'web_unknown';
		config.headers['DeviceId'] = deviceId;
	} else if (config.url.includes('/Login')) {
		config.headers['ScreenName'] = "Login";
		const deviceId = localStorage.getItem('deviceId') || 'web_unknown';
		config.headers['DeviceId'] = deviceId;
	}
	return config;
},
	(error) => {
		console.error('Request interceptor error:', error);
		return Promise.reject(error);
	}
)

axiosInstance.interceptors.response.use(
	async (response) => {
		if (response.data.statusCode === 401) {
			if (!isRefreshingToken) {
				const { error: refreshError, data } = await getrefreshToken()

				if (refreshError || !data || data.statusCode !== Success) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
					return Promise.reject(new Error('Session expired'))
				}

				store.commit('setToken', data.data.Token)
				store.commit('setRefreshToken', data.data.refreshToken)
				localStorage.setItem('refreshToken', data.data.refreshToken)
			}
		}
		return response
	},
	async (error) => {
		const originalRequest = error.config
		
		// **NEW: Handle network errors gracefully**
		if (!error.response) {
			console.error('Network error - no response from server:', error.message);
			// Return a structured error instead of rejecting
			return Promise.reject({
				message: 'Network Error',
				isNetworkError: true,
				originalError: error
			});
		}
		
		if (error?.response?.status === 401 && error?.response?.data?.statusCode === 401) {
			if (!isRefreshingToken) {
				const { error: refreshError, data } = await getrefreshToken()

				if (refreshError || !data || data.statusCode !== Success) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
					return Promise.reject(new Error('Session expired'))
				}

				error.config.headers['Authorization'] = data.data.token
				store.commit('setToken', data.data.Token || data.data.token)
				store.commit('setRefreshToken', data.data.refreshToken)
				localStorage.setItem('refreshToken', data.data.refreshToken)

				refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
					config.headers['Authorization'] = data.data.token
					axiosInstance
						.request(config)
						.then((response) => resolve(response))
						.catch((err) => reject(err))
				})

				refreshAndRetryQueue.length = 0
				return axiosInstance(originalRequest)
			}

			return new Promise((resolve, reject) => {
				refreshAndRetryQueue.push({ config: originalRequest, resolve, reject })
			})
		}
		
		// **NEW: Return structured error instead of raw rejection**
		return Promise.reject({
			message: error.response?.data?.message || error.message || 'An error occurred',
			status: error.response?.status,
			data: error.response?.data,
			originalError: error
		});
	}
)

export default () => {
	return axiosInstance
}

// Get feature status from server
export const getFeatureStatus = async () => {
  try {
    const response = await axios.get(`${baseURL}/api/patients/features`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch feature status:', error);
    throw error;
  }
};

// Patient API with conditional ABHA support
export const insertOrUpdatePatient = async (patientData) => {
  try {
    const endpoint = patientData.id 
      ? `${baseURL}/api/patients/update/${patientData.id}`
      : `${baseURL}/api/patients/register`;
    
    const response = await axios({
      method: patientData.id ? 'PUT' : 'POST',
      url: endpoint,
      data: patientData
    });
    
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

async function getrefreshToken() {
	isRefreshingToken = true
	const oldToken = store.getters.getToken
	const getrefreshToken = localStorage.getItem('refreshToken')

	if (getrefreshToken !== null) {
		try {
			const refreshToken = await axios.post(`${baseURL}/RefreshToken`, {}, {
				
				headers: {
					OS,
					OSVersion,
					AppVersion,
					Platform: 'Web',
					UserId: store.getters.getUserId,
					ScreenName: store.getters.getScreenName,
					DeviceId: localStorage.getItem('ip') ?? store.getters['locationStore/getLocationIpAddress'] ?? 'unknown',
					Authorization: getrefreshToken
				}
			})

			const newData = refreshToken.data?.data ?? refreshToken.data
			const newToken = newData?.Token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN:", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("A fresh token was issued")
			} else {
				console.log("Token is the same as before (no rotation)")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		} finally {
			isRefreshingToken = false
		}
	} else {
		try {
			const user = store.getters.getUser
			const response = await axios.post(
				`${baseURL}/Login`,
				{
					username: Vue.CryptoJS.AES.decrypt(user.userName, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					password: Vue.CryptoJS.AES.decrypt(user.password, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					city: store.getters['locationStore/getLocationCity'] ?? 'unknown',
					state: store.getters['locationStore/getLocationState'] ?? 'unknown',
					country: store.getters['locationStore/getLocationCountry'] ?? 'unknown',
				},
				{
					headers: {
						OS,
						OSVersion,
						AppVersion,
						Platform: 'Web',
						ScreenName: store.getters.getScreenName,
						DeviceId: localStorage.getItem('ip') ?? store.getters['locationStore/getLocationIpAddress'] ?? 'unknown'
					}
				}
			)

			const newData = response.data?.data ?? response.data
			const newToken = newData?.Token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN (from login):", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("New login token is different")
			} else {
				console.log("Login returned the same token")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		} finally {
			isRefreshingToken = false
		}
	}
}